import { FunctionComponent, useMemo } from 'react';
import { CourseOffer, OccurrenceOffer, SupportedCurrencies } from '@shared/types';
import {
    PublicCourse,
    PublicEducator,
    PublicOccurrence,
} from '@cademy-package/sdk-component-types';
import styles from './styles.module.scss';
import { renderPriceOrFree } from '../../../../services/renderPriceOrFree';
import { VATLabel } from '../../../VATLabel';
import { renderPrice } from '../../../../services/renderPrice';

const priceLabelFromOffers = (
    offers: Array<CourseOffer | OccurrenceOffer>,
    currency: SupportedCurrencies
): string => {
    const minimum = Math.min(...offers.map((offer) => offer.price || 0));
    const maximum = Math.max(...offers.map((offer) => offer.price || 0));
    if (minimum === maximum) {
        return renderPriceOrFree(minimum, currency);
    }
    return `${renderPriceOrFree(minimum, currency)} - ${renderPriceOrFree(maximum, currency)}`;
};

type DisplayPriceProps = {
    course: PublicCourse;
    educator: PublicEducator;
    occurrences: Array<PublicOccurrence>;
};

export const DisplayPrice: FunctionComponent<DisplayPriceProps> = ({
    course,
    educator,
    occurrences,
}) => {
    const priceLabel = useMemo(() => {
        if (course.type === 'on-request') {
            switch (course.pricing_option.type) {
                case 'free':
                    return 'Free';
                case 'price-on-enquiry':
                    return 'Price on Enquiry';
                case 'price-range':
                    return `${renderPriceOrFree(course.pricing_option.minimumPrice, course.currency)} - ${renderPrice(course.pricing_option.maximumPrice, course.currency)}`;
                case 'price-offers':
                    return priceLabelFromOffers(course.pricing_option.offers, course.currency);
            }
        }
        if (occurrences.length) {
            return priceLabelFromOffers(
                occurrences.flatMap((occurrence) => occurrence.offers || course.offers),
                course.currency
            );
        }
        return priceLabelFromOffers(course.offers, course.currency);
    }, [course, occurrences]);

    if (educator.feature_flags?.hide_pricing) {
        return null;
    }

    return (
        <div className={styles.price}>
            <div className={styles.priceLabel}>
                <span aria-hidden="true" className={styles.priceLabelText}>
                    {priceLabel}
                </span>
                <p className="screen_reader_hidden">{priceLabel}</p>
                <VATLabel
                    course={course}
                    priceLabel={priceLabel}
                    vatSettings={educator.vat_settings}
                />
            </div>
        </div>
    );
};
